/**
 * Shared API client: timeout, auth, and consistent error handling.
 * Use for all backend requests so behaviour is professional and predictable.
 */

const DEFAULT_TIMEOUT_MS = 15000;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function resolveFullUrl(url) {
  if (url.startsWith('http')) return url;
  const base = BASE_URL.replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${base}${path}`;
}

function hasContentTypeHeader(headers) {
  if (!headers) return false;
  return Object.keys(headers).some((k) => k.toLowerCase() === 'content-type');
}

/**
 * Same-origin / simple CORS request via XMLHttpRequest.
 * Avoids window.fetch (often wrapped by analytics / session replay) which can call
 * Response.clone() and leave bodies disturbed — producing "Failed to execute 'clone' on 'Response'".
 */
function xhrRequest(fullUrl, options = {}, timeoutMs) {
  const {
    method = 'GET',
    headers = {},
    body,
    signal,
    credentials,
  } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, fullUrl, true);
    xhr.timeout = timeoutMs;
    xhr.withCredentials = credentials === 'include';

    Object.entries(headers).forEach(([key, value]) => {
      if (value != null && value !== '') xhr.setRequestHeader(key, String(value));
    });

    const onAbort = () => {
      try {
        xhr.abort();
      } catch {
        /* ignore */
      }
    };
    if (signal) {
      if (signal.aborted) {
        reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    xhr.onload = () => {
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText || '',
        bodyText: typeof xhr.responseText === 'string' ? xhr.responseText : '',
      });
    };
    xhr.onerror = () => {
      if (signal) signal.removeEventListener('abort', onAbort);
      reject(new TypeError('Network error'));
    };
    xhr.onabort = () => {
      if (signal) signal.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
    };
    xhr.ontimeout = () => {
      if (signal) signal.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('timeout'), { name: 'AbortError' }));
    };

    xhr.send(body === undefined || body === null ? null : body);
  });
}

/**
 * Fetch with timeout. Rejects with a descriptive error on timeout or network failure.
 * @param {string} url - Full URL or path (if path, BASE_URL is prepended)
 * @param {RequestInit} options - fetch options
 * @param {number} timeoutMs - timeout in ms
 * @returns {Promise<Response>}
 */
export function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const fullUrl = resolveFullUrl(url);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  // Do not chain .finally() on the native fetch promise — in some browsers it can interfere
  // with Response body / clone semantics when combined with DevTools or other tooling.
  return (async () => {
    try {
      return await fetch(fullUrl, {
        ...options,
        signal: controller.signal,
        headers: {
          ...(options.body && typeof options.body === 'string' && !hasContentTypeHeader(options.headers)
            ? { 'Content-Type': 'application/json' }
            : {}),
          ...options.headers,
        },
      });
    } finally {
      clearTimeout(timeoutId);
    }
  })();
}

/**
 * Read fetch Response body once.
 * Do not use response.clone() here: clone() throws TypeError ("body is already used") when the
 * stream is already disturbed/locked, which shows up on failed logins and other 4xx paths in
 * some browsers / DevTools / extensions. A single .text() is the standard one-shot read.
 * @param {Response} response
 * @returns {Promise<string>}
 */
export async function readResponseBodyText(response) {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

/** When body could not be read, map status to a sensible message (e.g. auth). */
export function fallbackErrorForStatus(status, statusText = '') {
  if (status === 401) return 'Invalid credentials';
  if (status === 403) return 'Access denied';
  if (status === 404) return 'Not found';
  if (status >= 500) return statusText || `Server error (${status})`;
  return statusText || `Request failed (${status})`;
}

/**
 * Get auth header for admin, driver, or client.
 * @param {string} type - 'admin' | 'driver' | 'client'
 * @returns {{ Authorization: string } | {}}
 */
export function getAuthHeader(type = 'admin') {
  const key = type === 'admin' ? 'admin_token' : type === 'driver' ? 'driver_token' : 'session_token';
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Parse FastAPI-style error JSON from a response body string (single read — avoids double-consuming fetch body).
 * @param {string} text
 * @param {number} status
 * @param {string} [statusText]
 * @returns {string}
 */
export function parseErrorFromBodyText(text, status, statusText = '') {
  try {
    if (!text || !text.trim()) return statusText || `Error ${status}`;
    const body = JSON.parse(text);
    const d = body?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length) {
      const first = d[0];
      const msg = first?.msg ?? first?.loc?.join?.('. ');
      return msg ? String(msg) : JSON.stringify(d[0]);
    }
    if (d && typeof d === 'object') return JSON.stringify(d);
    return statusText || `Error ${status}`;
  } catch {
    return text.trim().slice(0, 300) || statusText || `Error ${status}`;
  }
}

/**
 * Parse error body from API (consumes response body once via .text()).
 * @param {Response} response
 * @returns {Promise<string>} Human-readable error message
 */
export async function parseErrorDetail(response) {
  const text = await readResponseBodyText(response);
  if (!text || !text.trim()) {
    return fallbackErrorForStatus(response.status, response.statusText);
  }
  return parseErrorFromBodyText(text, response.status, response.statusText);
}

const ORDERS_PAGE_MAX = 500;
const ORDERS_MAX_PAGES = 25;

/**
 * Load all orders visible to the current user via GET /api/orders/paged (multiple small responses).
 * Falls back to a single legacy GET /api/orders if the paged endpoint is unavailable.
 *
 * @param {{ headers: Record<string, string>, pageSize?: number, timeoutMs?: number, fetchImpl?: typeof fetchWithTimeout }} options
 * @returns {Promise<any[]>}
 */
export async function fetchAllOrdersPaged(options = {}) {
  const {
    headers,
    pageSize = 400,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchImpl = fetchWithTimeout,
  } = options;

  if (!headers || !headers.Authorization) {
    throw new Error('fetchAllOrdersPaged: Authorization header is required');
  }

  const size = Math.min(ORDERS_PAGE_MAX, Math.max(1, Number(pageSize) || 400));
  const merged = [];
  let skip = 0;
  let total = Infinity;
  let pages = 0;

  while (skip < total && pages < ORDERS_MAX_PAGES) {
    const url = `/api/orders/paged?limit=${size}&skip=${skip}`;
    const res = await fetchImpl(url, { headers: { ...headers } }, timeoutMs);
    if (res.status === 404) {
      const legacy = await fetchImpl('/api/orders', { headers: { ...headers } }, timeoutMs);
      if (!legacy.ok) {
        const detail = await parseErrorDetail(legacy);
        throw new Error(detail);
      }
      const legacyText = await readResponseBodyText(legacy);
      try {
        return legacyText ? JSON.parse(legacyText) : [];
      } catch {
        throw new Error('Invalid JSON in orders response');
      }
    }
    if (!res.ok) {
      const detail = await parseErrorDetail(res);
      throw new Error(detail);
    }
    const text = await readResponseBodyText(res);
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error('Invalid JSON in orders paged response');
    }
    const batch = Array.isArray(data.orders) ? data.orders : [];
    total = typeof data.total === 'number' ? data.total : skip + batch.length;
    merged.push(...batch);
    skip += batch.length;
    pages += 1;
    if (batch.length === 0) break;
    if (merged.length >= total) break;
  }
  return merged;
}

/**
 * Perform an API request with timeout and optional auth.
 * On non-2xx, returns { ok: false, error: string, status }.
 * On success, returns { ok: true, data } (parsed JSON).
 * On network/timeout, returns { ok: false, error: string }.
 *
 * @param {string} url - Path (e.g. '/api/auth/admin/login') or full URL
 * @param {RequestInit} options - fetch options (method, body, etc.)
 * @param {{ timeout?: number, auth?: 'admin'|'driver'|'client', parseJson?: boolean }} opts
 * @returns {Promise<{ ok: true, data: any } | { ok: false, error: string, status?: number }>}
 */
export async function apiRequest(url, options = {}, opts = {}) {
  const { timeout = DEFAULT_TIMEOUT_MS, auth, parseJson = true } = opts;
  const headers = {
    ...(options.body && typeof options.body === 'string' && !hasContentTypeHeader(options.headers)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...options.headers,
    ...(auth ? getAuthHeader(auth) : {}),
  };

  const fullUrl = resolveFullUrl(url);
  const xhrOptions = {
    method: options.method || 'GET',
    headers,
    body: options.body,
    signal: options.signal,
    credentials: options.credentials,
  };

  try {
    const { ok, status, statusText, bodyText: text } = await xhrRequest(fullUrl, xhrOptions, timeout);
    if (!ok) {
      const trimmed = text && text.trim();
      const error = trimmed
        ? parseErrorFromBodyText(text, status, statusText)
        : fallbackErrorForStatus(status, statusText);
      return { ok: false, error, status };
    }
    if (!parseJson) {
      return { ok: true, data: text };
    }
    try {
      const data = text ? JSON.parse(text) : null;
      return { ok: true, data };
    } catch {
      return {
        ok: false,
        error: 'Invalid JSON in server response',
        status,
      };
    }
  } catch (e) {
    const message =
      e?.name === 'AbortError'
        ? 'Request timed out. Please try again.'
        : e?.message || 'Network error. Please check your connection.';
    return { ok: false, error: message };
  }
}

export default {
  fetchWithTimeout,
  fetchAllOrdersPaged,
  getAuthHeader,
  parseErrorDetail,
  parseErrorFromBodyText,
  readResponseBodyText,
  fallbackErrorForStatus,
  apiRequest,
};
